import React, { useState } from 'react';
import { Checkbox, Statistic, Icon, Input, Button, Tooltip } from 'antd';
import moment from 'moment';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import numeral from 'numeral';
import Table from '../../common/components/Table';
import DateRangePicker from '../../common/components/DateRangePicker';
import Card from '../../common/components/Card';
import Profile from '../../common/components/Profile';
import ResourcePerformanceDetail from './ResourcePerformanceDetail';
import { showError } from '../../common/notifications';
import { ExportToCsv } from 'export-to-csv';

export const PERFORMANCE = gql`
  query performance($startDate: String, $endDate: String) {
    users {
      _id
      firstName
      lastName
      emails {
        address
      }
      title
      category
      imageUrl
      employeeType
      performance(startDate: $startDate, endDate: $endDate) {
        numOfWeeks
        timeReported
        leftOverHours
        pctUtilized
        hoursAvailable
        billableTime
        pctBillable
        nonBillableTime
        pctNonBillable
        holidayTime
        pctHoliday
        ptoTime
        pctPtoTime
      }
    }
  }
`;
const getUtilizationColor = utilizationPct => {
  if (utilizationPct < 80) {
    return 'red';
  }
  if (utilizationPct >= 80 && utilizationPct < 95) {
    return 'orange';
  }
  if (utilizationPct >= 95 && utilizationPct <= 105) {
    return 'green';
  }
  if (utilizationPct > 105 && utilizationPct <= 115) {
    return 'orange';
  }
  if (utilizationPct > 115) {
    return 'red';
  }
  return undefined;
};

const getBillableColor = billablePct => {
  if (billablePct > 100) {
    return 'green';
  }
  if (billablePct > 80 && billablePct <= 100) {
    return 'green';
  }
  if (billablePct > 50 && billablePct <= 80) {
    return 'orange';
  }
  if (billablePct <= 50) {
    return 'red';
  }
  return undefined;
};

const getNonBillableColor = nonBillablePct => {
  if (nonBillablePct >= 0 && nonBillablePct < 20) {
    return 'green';
  }
  if (nonBillablePct >= 20 && nonBillablePct < 50) {
    return 'orange';
  }
  if (nonBillablePct >= 50) {
    return 'red';
  }
  return undefined;
};

const getColumns = ({ searchInput, onSearch, resetSearch }) => [
  {
    dataIndex: 'index',
    render: value => <div className="font-medium text-base">{value + 1}</div>,
  },
  {
    title: 'Resource',
    key: 'name',
    width: '40%',
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={node => {
            searchInput = node;
          }}
          placeholder={`Search Name`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => onSearch(selectedKeys, confirm, 'firstName')}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button
          type="primary"
          onClick={() => onSearch(selectedKeys, confirm, 'firstName')}
          icon="search"
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Search
        </Button>
        <Button onClick={() => resetSearch(clearFilters)} size="small" style={{ width: 90 }}>
          Reset
        </Button>
      </div>
    ),
    filterIcon: filtered => (
      <Icon type="search" style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      const { firstName, lastName, title, emails } = record;
      const searchString = firstName + lastName + title + emails[0].address;
      return searchString.toString().toLowerCase().includes(value.toLowerCase());
    },
    onFilterDropdownVisibleChange: visible => {
      if (visible) {
        setTimeout(() => searchInput.select());
      }
    },
    render: ({ firstName, lastName, imageUrl, title, emails, employeeType }) => {
      return (
        <div>
          <Profile
            imageUrl={imageUrl}
            title={title}
            firstName={firstName}
            lastName={lastName}
            employeeType={employeeType}
            email={emails[0].address}
          />
        </div>
      );
    },
  },
  {
    // title: 'Utilization',
    key: 'performance',
    dataIndex: 'performance',
    width: '20%',
    sorter: ({ performance: aPerformance }, { performance: bPerformance }) => {
      const aUtilizationPct = (aPerformance.timeReported / aPerformance.hoursAvailable) * 100;
      const bUtilizationPct = (bPerformance.timeReported / bPerformance.hoursAvailable) * 100;
      return aUtilizationPct - bUtilizationPct;
    },
    render: ({ hoursAvailable, timeReported, pctUtilized }) => {
      const utilizationPct = pctUtilized * 100;
      const utilizationColor = getUtilizationColor(utilizationPct);

      return (
        <div className="flex justify-between">
          <div>
            <Statistic
              title="Utilization"
              value={numeral(utilizationPct).format('0.0')}
              valueStyle={{ color: utilizationColor }}
              suffix="%"
            />
            <Statistic
              title="Hours Reported"
              value={numeral(timeReported).format('0')}
              suffix={`/ ${hoursAvailable} hours available`}
            />
          </div>
        </div>
      );
    },
  },
  {
    // title: 'Billable',
    key: 'billable',
    dataIndex: 'performance',
    width: '20%',
    sorter: ({ performance: aPerformance }, { performance: bPerformance }) => {
      const aUtilizationPct = aPerformance.pctBillable * 100;
      const bUtilizationPct = bPerformance.pctBillable * 100;
      return aUtilizationPct - bUtilizationPct;
    },
    render: ({ billableTime, pctBillable }) => {
      const billablePct = pctBillable * 100;
      const billableColor = getBillableColor(billablePct);

      return (
        <div className="flex justify-between">
          <div>
            <Statistic
              title="Billable"
              value={numeral(billablePct).format('0.0')}
              valueStyle={{ color: billableColor }}
              suffix="%"
            />
            <Statistic title="Billable Hours" value={numeral(billableTime).format('0.0')} />
          </div>
        </div>
      );
    },
  },
  {
    // title: 'NonBillable',
    key: 'nonbillable',
    dataIndex: 'performance',
    width: '20%',
    sorter: ({ performance: aPerformance }, { performance: bPerformance }) => {
      const aUtilizationPct = aPerformance.pctNonBillable * 100;
      const bUtilizationPct = bPerformance.pctNonBillable * 100;
      return aUtilizationPct - bUtilizationPct;
    },
    render: ({ nonBillableTime, pctNonBillable, hoursAvailable }) => {
      const nonBillablePct = pctNonBillable * 100;
      const nonBillableColor = getNonBillableColor(nonBillablePct);

      return (
        <div className="flex justify-between">
          <div>
            <Statistic
              title="Non Billable"
              value={numeral(nonBillablePct).format('0.0')}
              valueStyle={{ color: nonBillableColor }}
              suffix="%"
            />
            <Statistic title="Non Billable Hours" value={numeral(nonBillableTime).format('0.0')} />
          </div>
        </div>
      );
    },
  },
];


const calculateData = ({ filters, data, ignoreInactive }) => {
  const includeEmployees = filters.includes('employees');
  const includeContractors = filters.includes('contractors');
  const users =
    data?.users
      ?.filter(({ performance }) => !!performance)
      ?.filter(({ category }) => {
        if (filters.includes('exclude-overhead') && category === 'Overhead') {
          return false;
        }
        return true;
      })
      ?.filter(({ performance }) => {
        if (ignoreInactive) {
          const { timeReported } = performance;
          return timeReported !== 0;
        }
        return true;
      })
      .filter(({ employeeType, category, performance }) => {
        if (!includeEmployees && !includeContractors) {
          return false;
        }
        let typeMatch = false;
        if (includeEmployees && employeeType === 'W2') {
          typeMatch = true;
        }
        if (includeContractors && employeeType === 'C2C') {
          typeMatch = true;
        }
        if (!typeMatch) {
          return false;
        }
        if (filters.length === 0) {
          return true;
        }
        const { pctUtilized } = performance;
        const utilization = pctUtilized * 100;
        const utilizationFilters = filters.filter(f => f === 'underutilized' || f === 'overutilized');
        if (utilizationFilters.length === 0) {
          return true;
        }
        return utilizationFilters.some(filter => {
          switch (filter) {
            case 'underutilized':
              return utilization < 90;
            case 'overutilized':
              return utilization > 110;
            default:
              return false;
          }
        });
      })
      .sort((a, b) => {
        const aUtil = a.performance.timeReported / a.performance.hoursAvailable;
        const bUtil = b.performance.timeReported / b.performance.hoursAvailable;
        return bUtil - aUtil;
      }) || [];

  const { hoursAvailable, numOfWeeks } = users.length > 0 ? users[0].performance : {};
  const totalResources = users.length;
  const overallUtilization =
    users.reduce((acc, cur) => acc + cur.performance.pctUtilized * 100, 0) / totalResources;
  const overallBillable =
    users.reduce((acc, cur) => acc + cur.performance.pctBillable * 100, 0) / totalResources;
  const overallNonBillable =
    users.reduce((acc, cur) => acc + cur.performance.pctNonBillable * 100, 0) / totalResources;

  return {
    users,
    totalResources,
    hoursAvailable,
    numOfWeeks,
    overallUtilization,
    overallBillable,
    overallNonBillable,
  };
};

const ResourceUtilization = () => {
  const [dateRange, setDateRange] = useState([
    moment().subtract(1, 'month').startOf('month').format('YYYYMMDD'),
    moment().subtract(1, 'month').endOf('month').format('YYYYMMDD'),
  ]);
  const [searchInput, setSearchInput] = useState(null);
  const [filters, setFilters] = useState([
    'employees',
    'contractors',
    'overutilized',
    'underutilized',
  ]);
  const [ignoreInactive, setIgnoreInactive] = useState(true);
  const { loading, data = {} } = useQuery(PERFORMANCE, {
    variables: {
      startDate: dateRange[0],
      endDate: dateRange[1],
    },
  });

  const {
    users,
    totalResources,
    hoursAvailable,
    numOfWeeks,
    overallUtilization,
    overallBillable,
    overallNonBillable,
  } = calculateData({ filters, data, ignoreInactive });

  return (
    <Card
      border
      floating
      title={
        <>
          Resource Utilization <sup>(beta)</sup>
        </>
      }
      actionComponent={
        <DateRangePicker
          startDate={dateRange[0]}
          endDate={dateRange[1]}
          ranges={{
            YTD: [moment().startOf('year'), moment()],
            QTD: [moment().startOf('quarter'), moment()],
            'Last Year': [
              moment().startOf('year').subtract(1, 'year'),
              moment().startOf('year').subtract(1, 'day'),
            ],
            'Last Quarter': [
              moment().startOf('quarter').subtract(1, 'quarter'),
              moment().startOf('quarter').subtract(1, 'day'),
            ],
            'Last Month': [
              moment().subtract(1, 'month').startOf('month'),
              moment().subtract(1, 'month').endOf('month'),
            ],
            'Current Month': [moment().startOf('month'), moment().endOf('month')],
          }}
          handleDateChange={([startDate, endDate]) => {
            const diff = endDate.diff(startDate, 'days');
            if (diff < 31) {
              setDateRange([startDate.format('YYYYMMDD'), endDate.format('YYYYMMDD')]);
            } else {
              showError({ duration: 7, message: 'The selected date range is too large.' });
            }
          }}
        />
      }
    >
      <div className="p-4 flex justify-between">
        <Checkbox checked={ignoreInactive} onChange={e => setIgnoreInactive(e.target.checked)}>
          Ignore Inactive
        </Checkbox>
        <Checkbox.Group
          options={[
            { label: 'Overutilized', value: 'overutilized' },
            { label: 'Underutilized', value: 'underutilized' },
            { label: 'Contractors', value: 'contractors' },
            { label: 'Employees', value: 'employees' },
            { label: 'Exclude Overhead', value: 'exclude-overhead' },
          ]}
          value={filters}
          onChange={filters => setFilters(filters)}
        />
        <Button
          onClick={() => {
            const userExportData = users.map(user => ({
              name: `${user.firstName} ${user.lastName}`,
              email: user.emails[0]?.address || '',
              title: user.title || '',
              category: user.category || '',
              employeeType: user.employeeType || '',
              hoursAvailable: user.performance?.hoursAvailable || 0,
              timeReported: user.performance?.timeReported || 0,
              utilizationPct: numeral(user.performance?.pctUtilized * 100).format('0.0'),
              billableHours: user.performance?.billableTime || 0,
              billablePct: numeral(user.performance?.pctBillable * 100).format('0.0'),
              nonBillableHours: user.performance?.nonBillableTime || 0,
              nonBillablePct: numeral(user.performance?.pctNonBillable * 100).format('0.0'),
              holidayHours: user.performance?.holidayTime || 0,
              ptoHours: user.performance?.ptoTime || 0,
              leftOverHours: user.performance?.leftOverHours || 0
            }));

            const options = {
              fieldSeparator: ',',
              quoteStrings: '"',
              decimalSeparator: '.',
              showLabels: true,
              showTitle: false,
              useTextFile: false,
              useBom: true,
              useKeysAsHeaders: false,
              filename: `Resource_Utilization_${moment(dateRange[0]).format('YYYYMMDD')}-${moment(
                dateRange[1],
              ).format('YYYYMMDD')}_ExportedOn_${moment().format('YYYYMMDD_HHmm')}`,
              headers: [
                'Name',
                'Email',
                'Title',
                'Category',
                'Employee Type',
                'Hours Available',
                'Time Reported',
                'Utilization %',
                'Billable Hours',
                'Billable %',
                'Non-Billable Hours',
                'Non-Billable %',
                'Holiday Hours',
                'PTO Hours',
                'Left Over Hours'
              ]
            };

            const csvExporter = new ExportToCsv(options);
            csvExporter.generateCsv(userExportData);
          }}
        >
          Export
        </Button>
      </div>
      <div className="flex justify-between p-4">
        <Statistic title="Total Resources" value={numeral(totalResources).format('0')} />
        <Statistic
          title="Overall Utilization"
          value={numeral(overallUtilization).format('0.0')}
          suffix="%"
        />
        <Statistic
          title="Overall Billable"
          value={numeral(overallBillable).format('0.0')}
          suffix="%"
        />
        <Statistic
          title="Overall Non-Billable"
          value={numeral(overallNonBillable).format('0.0')}
          suffix="%"
        />
        <Statistic
          title={
            <span>
              Available Hours{' '}
              <Tooltip title="Total number of hours a user is expected to work in the selected range, minus company holidays. Calculated as: (working days × 8) − (holidays × 8)">
                <Icon type="info-circle" />
              </Tooltip>
            </span>
          }
          value={numeral(hoursAvailable).format('0.0')}
        />
        <Statistic
          title={
            <span>
              Number of Weeks{' '}
              <Tooltip title="Number of working weeks (Monday–Friday) in the selected date range. Calculated as the number of weekdays divided by 5.">
                <Icon type="info-circle" />
              </Tooltip>
            </span>
          }
          value={numeral(numOfWeeks).format('0.0')}
        />
      </div>
      <Table
        loading={loading}
        columns={getColumns({
          searchInput,
          resetSearch: clearFilters => {
            clearFilters();
            setSearchInput(null);
          },
          onSearch: (selectedKeys, confirm) => {
            confirm();
            setSearchInput(selectedKeys[0]);
          },
        })}
        expandedRowRender={user => (
          <ResourcePerformanceDetail userId={user._id} dateRange={dateRange} />
        )}
        pagination={{
          pageSize: 50,
        }}
        locale={{
          emptyText: 'No timesheets for that range',
        }}
        dataSource={users.map((u, index) => ({ ...u, key: u._id, index }))}
      />
    </Card>
  );
};

export default ResourceUtilization;
